import { useState, useRef } from 'react';
import { Camera, Upload, X, Image as ImageIcon, MagnifyingGlassPlus } from '@phosphor-icons/react';
import axios from 'axios';
import { Button } from './ui/button';
import { ImageLightbox } from './ImageLightbox';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PhotoUpload = ({ photos = [], onPhotosChange, maxPhotos = 10, readOnly = false }) => {
  const [uploading, setUploading] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    if (readOnly) return;
    const files = Array.from(e.target.files);
    if (photos.length + files.length > maxPhotos) {
      alert(`Solo puedes subir hasta ${maxPhotos} fotos`);
      return;
    }

    setUploading(true);
    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/upload`, formData);
        return response.data.url;
      });

      const urls = await Promise.all(uploadPromises);
      onPhotosChange([...photos, ...urls]);
    } catch (error) {
      logger.error('Error uploading photos:', error);
      alert('Error al subir las fotos');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    if (readOnly) return;
    const newPhotos = photos.filter((_, i) => i !== index);
    onPhotosChange(newPhotos);
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="text-xs font-bold uppercase tracking-[0.2em] text-foreground">
          Fotografías ({photos.length}/{maxPhotos})
        </label>
        <div className="flex items-center gap-2">
          {photos.length > 0 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => openLightbox(0)}
              data-testid="open-gallery-button"
              className="hover:bg-primary hover:text-primary-foreground"
            >
              <MagnifyingGlassPlus size={16} className="mr-2" />
              Ver Galería
            </Button>
          )}
          {!readOnly && photos.length < maxPhotos && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              data-testid="upload-photo-button"
              className="hover:bg-primary hover:text-primary-foreground"
            >
              <Upload size={16} className="mr-2" />
              {uploading ? 'Subiendo...' : 'Subir Foto'}
            </Button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          disabled={readOnly}
        />
      </div>

      {photos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo, index) => (
            <div
              key={photo}
              className="relative aspect-square border border-border bg-secondary group cursor-pointer overflow-hidden"
              data-testid={`photo-preview-${index}`}
              onClick={() => openLightbox(index)}
            >
              <img
                src={photo}
                alt={`Foto ${index + 1}`}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
              />
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <MagnifyingGlassPlus 
                  size={32} 
                  className="text-white opacity-0 group-hover:opacity-100 transition-opacity" 
                  weight="bold"
                />
              </div>
              {!readOnly && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removePhoto(index);
                  }}
                  data-testid={`remove-photo-${index}`}
                  className="absolute top-2 right-2 p-1 bg-destructive text-destructive-foreground opacity-0 group-hover:opacity-100 z-10"
                >
                  <X size={16} weight="bold" />
                </button>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="border-2 border-dashed border-border p-12 text-center">
          <ImageIcon size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">No hay fotografías</p>
        </div>
      )}

      {/* Lightbox Modal */}
      <ImageLightbox
        images={photos}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={closeLightbox}
      />
    </div>
  );
};
